import { getTags } from '../../../apis/apiLib';
import { apiSlice, ErrorType } from '../../../apis/api';
import { CalculationsCollection } from '../../../swagger/collections';
import {
  CalculationRoutesDeleteRequest,
  CalculationSourceStoresDeleteRequest,
  CalculationTargetStoresDeleteRequest,
  CalculationDeletePickingPositionsRequest,
  SkuRedistributionChangelog,
} from '@ydistri/api-sdk';

const { TAGS, TAGS_ARRAY } = getTags('calculationEdit');
const { TAGS: TAGS_REDISTRIBUTION } = getTags('calculationCategories');
const { TAGS: TAGS_STATISTICS } = getTags('calculationStatistics2');

// Reusable helper to adapt an async SDK call to RTK Query's queryFn shape for void-returning endpoints
// Accept any Promise (Axios returns a Promise<AxiosResponse<...>>) and ignore its value
export function runVoid(
  fn: () => Promise<unknown>,
): Promise<{ data: undefined } | { error: ErrorType }> {
  return (
    fn()
      .then(() => ({ data: undefined }))
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the error type
      .catch((error: unknown) => ({ error: error as ErrorType }))
  );
}

interface DeleteRoutesRequest {
  calculationId: number;
  routes: CalculationRoutesDeleteRequest[];
}

interface DeleteSourceStoresRequest {
  calculationId: number;
  sourceStores: CalculationSourceStoresDeleteRequest[];
}

interface DeleteTargetStoresRequest {
  calculationId: number;
  targetStores: CalculationTargetStoresDeleteRequest[];
}

interface DeletePickingPositionsRequest {
  calculationId: number;
  pickingPositions: CalculationDeletePickingPositionsRequest[];
}

const getTagsToInvalidate = (calculationId: number) => [
  { type: TAGS.changelog, id: calculationId },
  { type: TAGS_REDISTRIBUTION.calculationCategoryPairings, id: 'LIST' },
  { type: TAGS_STATISTICS.calculationStats },
  { type: TAGS_STATISTICS.statsRouteSummaries },
  { type: TAGS_STATISTICS.statsRoutes },
  { type: TAGS_STATISTICS.statsStoreSummaries },
  { type: TAGS_STATISTICS.statsSourceStores },
  { type: TAGS_STATISTICS.statsRequestStores },
  { type: TAGS_STATISTICS.statsRegions },
  { type: TAGS_STATISTICS.statsSkuClasses },
  { type: TAGS_STATISTICS.statsStoreTypes },
  { type: TAGS_STATISTICS.statsRequestListSummary },
  { type: TAGS_STATISTICS.statsRequestListItems },
  { type: TAGS_STATISTICS.statsRequestListItemSources },
];

export const apiCalculationEdit = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getChangelog: builder.query<SkuRedistributionChangelog[], number>({
        queryFn: async calculationId => {
          const response = await CalculationsCollection.skuRedistributionChangelogs(calculationId);
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [{ type: TAGS.changelog, id: arg }],
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      deleteRoutes: builder.mutation<void, DeleteRoutesRequest>({
        queryFn: arg =>
          runVoid(() => CalculationsCollection.deleteRoutes(arg.calculationId, arg.routes)),
        invalidatesTags: (r, e, arg) => getTagsToInvalidate(arg.calculationId),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      deleteSourceStores: builder.mutation<void, DeleteSourceStoresRequest>({
        queryFn: arg =>
          runVoid(() =>
            CalculationsCollection.deleteSourceStores(arg.calculationId, arg.sourceStores),
          ),
        invalidatesTags: (r, e, arg) => getTagsToInvalidate(arg.calculationId),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      deleteTargetStores: builder.mutation<void, DeleteTargetStoresRequest>({
        queryFn: arg =>
          runVoid(() =>
            CalculationsCollection.deleteTargetStores(arg.calculationId, arg.targetStores),
          ),
        invalidatesTags: (r, e, arg) => getTagsToInvalidate(arg.calculationId),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      deletePickingPositions: builder.mutation<void, DeletePickingPositionsRequest>({
        queryFn: arg =>
          runVoid(() =>
            CalculationsCollection.deletePickingPositions(arg.calculationId, arg.pickingPositions),
          ),
        invalidatesTags: (r, e, arg) => getTagsToInvalidate(arg.calculationId),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      backupSkuRedistribution: builder.mutation<void, number>({
        queryFn: arg => runVoid(() => CalculationsCollection.backupSkuRedistribution(arg)),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      restoreSkuRedistribution: builder.mutation<void, number>({
        queryFn: arg => runVoid(() => CalculationsCollection.restoreSkuRedistribution(arg)),
        invalidatesTags: (r, e, arg) => getTagsToInvalidate(arg),
      }),
    }),
  });

export const {
  useGetChangelogQuery,
  useDeleteRoutesMutation,
  useDeleteSourceStoresMutation,
  useDeleteTargetStoresMutation,
  useDeletePickingPositionsMutation,
  useBackupSkuRedistributionMutation,
  useRestoreSkuRedistributionMutation,
} = apiCalculationEdit;
